import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, ExternalLink, History, Loader2, Pencil, Plus, Power, PowerOff, Save, Search, Upload } from "lucide-react";
import { CostImportDialog } from "@/components/admin/CostImportDialog";
import { toast } from "sonner";
import { useApp } from "@/store/useApp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { ApiError } from "@/lib/api";
import { hasPermission } from "@/lib/types";
import { nfDanfeAccessKey, referenceLabel } from "@/lib/costHistoryFormat";
import { viewSupplierInvoicePdf } from "@/lib/adminSupplierInvoicesApi";
import {
  MATERIAL_UNIT_LABELS,
  createPackagingMaterial,
  fetchPackagingMaterialHistory,
  fetchPackagingMaterials,
  setPackagingMaterialActive,
  updatePackagingMaterial,
  type MaterialUnit,
  type PackagingMaterial,
  type PackagingMaterialCostHistoryEntry,
} from "@/lib/adminPackagingMaterialsApi";

const brl = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
const fmtCost = (v: number | null) => (v == null ? "—" : brl.format(v));
const parseDecimal = (raw: string): number | null => {
  const t = raw.trim();
  if (t === "") return null;
  const n = Number(t.replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

/** Apresenta o valor registrado: campos monetários como R$ (preservando o dado); "—" = ausência (≠ zero). */
const MONETARY_FIELDS = new Set(["Custo"]);
function displayHistoryValue(label: string, value: string): string {
  if (value === "—" || value.trim() === "") return "—"; // ausência (distinta de zero)
  if (MONETARY_FIELDS.has(label)) {
    const n = Number(value.replace(",", "."));
    return Number.isFinite(n) ? brl.format(n) : value; // zero → R$ 0,00
  }
  return value;
}

/** Pareia as linhas "Campo: valor" de oldValues/newValues já registradas → "Campo: anterior → novo". */
function pairHistory(oldValues: string, newValues: string): { label: string; from: string; to: string }[] {
  const split = (s: string) =>
    (s ?? "").split("\n").filter((l) => l.trim() !== "").map((line) => {
      const i = line.indexOf(":");
      return i < 0 ? { label: line.trim(), value: "" } : { label: line.slice(0, i).trim(), value: line.slice(i + 1).trim() };
    });
  const olds = split(oldValues);
  const news = split(newValues);
  const n = Math.max(olds.length, news.length);
  const rows: { label: string; from: string; to: string }[] = [];
  for (let i = 0; i < n; i++) {
    rows.push({ label: news[i]?.label ?? olds[i]?.label ?? "", from: olds[i]?.value ?? "—", to: news[i]?.value ?? "—" });
  }
  return rows;
}

type Draft = {
  id?: string;
  name: string;
  materialUnit: MaterialUnit;
  currentCost: string;
  costReferenceDate: string;
  note: string;
  version: number | null;
};

const emptyDraft: Draft = {
  name: "",
  materialUnit: "UNIDADE",
  currentCost: "",
  costReferenceDate: "",
  note: "",
  version: null,
};

export default function AdminPackagingMaterials() {
  const account = useApp((s) => s.account);
  const canCreate = hasPermission(account, "SUPRIMENTOS.EMBALAGEM.CRIAR");
  const canEdit = hasPermission(account, "SUPRIMENTOS.EMBALAGEM.EDITAR");

  const [items, setItems] = useState<PackagingMaterial[]>([]);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">("loading");
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [historyOf, setHistoryOf] = useState<PackagingMaterial | null>(null);
  const [history, setHistory] = useState<PackagingMaterialCostHistoryEntry[] | null>(null);
  const [openingDanfe, setOpeningDanfe] = useState<string | null>(null);

  // Abre o "DANFE gerado do XML" da NF que originou o custo (nova aba; mantém o diálogo do histórico aberto).
  // Permissão do documento é enforçada no backend; indisponibilidade vira mensagem clara.
  const openDanfe = (accessKey: string) => {
    setOpeningDanfe(accessKey);
    viewSupplierInvoicePdf(accessKey)
      .catch((e) => toast.error(e instanceof Error ? e.message : "Não foi possível abrir o DANFE desta NF."))
      .finally(() => setOpeningDanfe(null));
  };
  const [importOpen, setImportOpen] = useState(false);

  const load = useCallback(async () => {
    setLoadState("loading");
    try {
      setItems(await fetchPackagingMaterials());
      setLoadState("ready");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível carregar os materiais");
      setLoadState("error");
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((m) => (showInactive || m.active) && (!q || m.name.toLowerCase().includes(q)));
  }, [items, query, showInactive]);

  const save = async () => {
    if (!draft) return;
    if (!draft.name.trim()) { toast.error("Informe o nome do material"); return; }
    setSaving(true);
    try {
      const payload = {
        name: draft.name.trim(),
        materialUnit: draft.materialUnit,
        currentCost: parseDecimal(draft.currentCost),
        costReferenceDate: draft.costReferenceDate || null,
        note: draft.note.trim() || null,
        supplierId: null,
        version: draft.version,
      };
      if (draft.id) await updatePackagingMaterial(draft.id, payload);
      else await createPackagingMaterial(payload);
      toast.success("Material salvo");
      setDraft(null);
      await load();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        toast.error("O material foi alterado por outra pessoa. Recarregando…");
        setDraft(null);
        await load();
      } else {
        toast.error(e instanceof Error ? e.message : "Não foi possível salvar o material");
      }
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (m: PackagingMaterial) => {
    try {
      await setPackagingMaterialActive(m.id, !m.active);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Não foi possível atualizar o material");
    }
  };

  const openHistory = async (m: PackagingMaterial) => {
    setHistoryOf(m);
    setHistory(null);
    try {
      setHistory(await fetchPackagingMaterialHistory(m.id));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Não foi possível carregar o histórico");
    }
  };

  return (
    <>
      <section className="bg-gradient-hero text-primary-foreground">
        <div className="container flex flex-col gap-4 py-8 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight">Embalagens</h1>
            <p className="mt-1 text-sm text-primary-foreground/80">
              Materiais de embalagem, unidade de custo e histórico — base da composição por apresentação.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canEdit && (
              <Button variant="outline" size="sm"
                className="border-primary-foreground/30 bg-primary-foreground/10 text-primary-foreground hover:bg-primary-foreground/20"
                onClick={() => setImportOpen(true)}>
                <Upload className="h-4 w-4" /> Importar XLSX
              </Button>
            )}
            {canCreate && (
              <Button variant="hero" className="bg-primary-foreground text-primary hover:bg-primary-foreground/90"
                onClick={() => setDraft({ ...emptyDraft })}>
                <Plus className="h-4 w-4" /> Novo material
              </Button>
            )}
          </div>
        </div>
      </section>

      <div className="container space-y-5 py-6">
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Buscar material..." value={query} onChange={(e) => setQuery(e.target.value)}
              className="h-14 bg-surface pl-12 text-base" />
          </div>
          <Button variant={showInactive ? "hero" : "outline"} onClick={() => setShowInactive((v) => !v)}
            className="h-14 shrink-0 px-5">
            {showInactive ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}
            {showInactive ? "Ocultar inativos" : "Mostrar inativos"}
          </Button>
        </div>

        {loadState === "loading" && (
          <div className="surface-card p-12 text-center text-sm text-muted-foreground">
            <Loader2 className="mx-auto mb-3 h-5 w-5 animate-spin text-primary" /> Carregando materiais...
          </div>
        )}
        {loadState === "error" && (
          <div className="surface-card p-12 text-center text-sm text-destructive">{error}</div>
        )}

        {loadState === "ready" && (
          <div className="surface-card overflow-hidden">
            <div className="hidden grid-cols-[1.6fr_1fr_1fr_1fr_auto] gap-4 border-b border-border bg-secondary/40 px-5 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground md:grid">
              <div>Material</div><div>Unidade de custo</div><div>Custo</div><div>Atualização</div>
              <div className="text-right">Ações</div>
            </div>
            <ul className="divide-y divide-border">
              {filtered.map((m) => (
                <li key={m.id} className={cn("grid items-center gap-4 px-5 py-3 md:grid-cols-[1.6fr_1fr_1fr_1fr_auto]", !m.active && "opacity-60")}>
                  <div className="flex items-center gap-2 font-display font-semibold">
                    <Box className="h-4 w-4 text-muted-foreground" /> {m.name}
                    {!m.active && <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] font-bold uppercase text-muted-foreground">Inativo</span>}
                  </div>
                  <div className="text-sm">{m.materialUnitLabel}</div>
                  <div className="text-sm tabular-nums">{fmtCost(m.currentCost)}</div>
                  <div className="text-xs text-muted-foreground">{m.updatedAt ? new Date(m.updatedAt).toLocaleDateString("pt-BR") : "—"}</div>
                  <div className="flex gap-2 md:justify-end">
                    <Button size="sm" variant="outline" onClick={() => void openHistory(m)}>
                      <History className="h-4 w-4" /> Histórico
                    </Button>
                    {canEdit && (
                      <Button size="sm" variant="outline" onClick={() => setDraft({
                        id: m.id, name: m.name, materialUnit: m.materialUnit,
                        currentCost: m.currentCost != null ? String(m.currentCost) : "",
                        costReferenceDate: m.costReferenceDate ?? "", note: m.note ?? "", version: m.version })}>
                        <Pencil className="h-4 w-4" /> Editar
                      </Button>
                    )}
                    {canEdit && (
                      <Button size="sm" variant="ghost" onClick={() => void toggleActive(m)}>
                        {m.active ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}
                        {m.active ? "Inativar" : "Reativar"}
                      </Button>
                    )}
                  </div>
                </li>
              ))}
              {filtered.length === 0 && (
                <li className="px-5 py-12 text-center text-sm text-muted-foreground">Nenhum material encontrado.</li>
              )}
            </ul>
          </div>
        )}
      </div>

      <Dialog open={draft !== null} onOpenChange={(o) => { if (!o) setDraft(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display text-xl">{draft?.id ? "Editar material" : "Novo material"}</DialogTitle></DialogHeader>
          {draft && (
            <div className="space-y-3 text-sm">
              <div className="space-y-1"><Label>Nome *</Label>
                <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Unidade de custo</Label>
                  <Select value={draft.materialUnit} onValueChange={(v) => setDraft({ ...draft, materialUnit: v as MaterialUnit })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(Object.keys(MATERIAL_UNIT_LABELS) as MaterialUnit[]).map((u) => (
                        <SelectItem key={u} value={u}>{MATERIAL_UNIT_LABELS[u]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label>Custo (R$)</Label>
                  <Input type="number" min={0} step="0.0001" inputMode="decimal" placeholder="Não cadastrado"
                    value={draft.currentCost} onChange={(e) => setDraft({ ...draft, currentCost: e.target.value })} className="tabular-nums" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Data de referência</Label>
                  <Input type="date" value={draft.costReferenceDate} onChange={(e) => setDraft({ ...draft, costReferenceDate: e.target.value })} /></div>
                <div className="space-y-1"><Label>Observação</Label>
                  <Input value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} placeholder="Opcional" /></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDraft(null)}>Cancelar</Button>
            <Button variant="hero" onClick={() => void save()} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={historyOf !== null} onOpenChange={(o) => { if (!o) { setHistoryOf(null); setHistory(null); } }}>
        <DialogContent className="max-h-[80vh] max-w-md overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-lg">Histórico — {historyOf?.name}</DialogTitle></DialogHeader>
          {history == null ? (
            <div className="py-6 text-center text-sm text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
          ) : history.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Sem alterações registradas.</p>
          ) : (
            <div className="space-y-2">
              {history.map((h) => (
                <div key={h.id} className="rounded-md border border-border bg-background p-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{new Date(h.changedAt).toLocaleString("pt-BR")}</span>
                    <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">{h.origin}</span>
                  </div>
                  <div className="mt-1 text-muted-foreground">por {h.changedBy}</div>
                  <ul className="mt-1 space-y-0.5">
                    {pairHistory(h.oldValues, h.newValues).map((r, i) => (
                      <li key={i}>
                        <span className="font-medium">{r.label}:</span> {displayHistoryValue(r.label, r.from)}{" "}
                        <span className="text-muted-foreground">→</span> {displayHistoryValue(r.label, r.to)}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-1 text-muted-foreground">
                    Referência:{" "}
                    {(() => {
                      const accessKey = nfDanfeAccessKey(h);
                      if (!accessKey) return referenceLabel(h);
                      return (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 font-medium text-primary underline underline-offset-2 hover:opacity-80 disabled:opacity-60"
                          onClick={() => openDanfe(accessKey)}
                          disabled={openingDanfe === accessKey}
                          title="Abrir o DANFE gerado do XML em nova aba"
                        >
                          {referenceLabel(h)}
                          {openingDanfe === accessKey ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <ExternalLink className="h-3 w-3" />
                          )}
                        </button>
                      );
                    })()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <CostImportDialog
        open={importOpen}
        basePath="/api/v1/admin/packaging-materials/import"
        templateFilename="modelo_custos_embalagens.xlsx"
        title="Importar custos de materiais (XLSX)"
        canEdit={canEdit}
        onClose={() => setImportOpen(false)}
        onApplied={() => void load()}
      />
    </>
  );
}
